/**
 * The client half of ground-object-only (issue #356).
 *
 * Pure functions, no React and no network, for the reason every other
 * `lib/*.js` in this folder gives: this repo mounts no components in unit
 * tests, so a rule that lives inside JSX is only checkable by the e2e suite,
 * which does not run per-commit. The rules below decide whether a form may
 * be submitted and whether a legacy row can converge without asking the user
 * anything — both worth pinning down directly.
 *
 * THE MODEL. A ground field now holds a PAIR: the name the user sees, and
 * the id of the record behind it. `groundId` is the truth; `name` is the
 * label. A field with a name and no id is UNRESOLVED — it is what somebody
 * typed, and it is exactly what #356 stops the API from storing.
 */

/** What the form says when the user typed a place instead of picking one. */
export const GROUND_UNRESOLVED_MESSAGE = "Pick this ground from the list, or add it";

/** The API's machine-readable "this row predates ground records" refusal. */
export const REPICK_REQUIRED_CODE = "ground_repick_required";

/**
 * Is this field good enough to submit?
 *
 * A blank field is NOT resolved and NOT an error by itself — whether a blank
 * place is allowed is the caller's question (a match must have one, a
 * training session need not), and answering it here would bake the match
 * rule into the shared helper. See `groundFieldError`.
 */
export function isGroundResolved(name, groundId) {
  return Boolean(groundId) && Boolean((name || "").trim());
}

/**
 * THE ZERO-TAP CONVERGENCE (issue #356, product decision 2).
 *
 * Given a stored free-text name and the grounds the caller can see, return
 * the record it obviously means, or `null`.
 *
 * OBVIOUSLY MEANS: a case- and whitespace-insensitive match on the whole
 * name. Not a substring, not a prefix, not a fuzzy distance — this function
 * decides, without asking anybody, that a historical record refers to a
 * particular real place, and the only version of that judgement worth making
 * silently is the one where the two strings ARE the same string. "Torvale
 * Domain" resolving to "Tōrvale Domain" would be a guess; "thornmere park " to
 * "Thornmere Park" is the fragmentation #59 case-snapped, which is a spelling
 * variant of one name rather than a choice between two places.
 *
 * AMBIGUITY REFUSES. If two visible grounds share a name (a nets facility
 * and a field both called "Torvale Domain" is an ordinary directory state,
 * #136), there is no obvious answer and the user is asked. Returning the
 * first would silently attach half a club's matches to the wrong pin.
 *
 * This is what makes decision 2 liveable rather than hostile: a legacy match
 * whose name the directory already knows opens ALREADY VALID, and the player
 * saves their score fix without being asked anything at all.
 */
export function resolveStoredGround(name, grounds) {
  const key = (name || "").trim().toLowerCase();
  if (!key) return null;
  const hits = (grounds || []).filter((g) => (g?.name || "").trim().toLowerCase() === key);
  return hits.length === 1 ? hits[0] : null;
}

/**
 * The per-field error, or `undefined`. `required` is the caller's answer to
 * "must this field hold anything at all" — true for a match's ground, false
 * for a training session's location.
 */
export function groundFieldError(name, groundId, { required }) {
  const typed = (name || "").trim();
  if (!typed) return required ? "Ground is required" : undefined;
  return groundId ? undefined : GROUND_UNRESOLVED_MESSAGE;
}

/**
 * Does this API failure mean "re-pick the ground on a legacy row"?
 * Keyed on the `code`, never the prose — the sentence is allowed to change.
 */
export function isRepickRequired(error) {
  return error?.response?.data?.code === REPICK_REQUIRED_CODE;
}
